<template>
  <div>
    <el-button data-testid="surveillance-report" class="surveillance-report" :disabled="disabled" :plain="true" type="primary" size="small" @click="lineListReport()">Surveillance Report</el-button>
    <ProgressOverlay v-if="inProgress" :progress="progressValue" v-on:progress-cancelled="cancel" title="Report In Progress" />
  </div>
</template>

<script>
import ProgressOverlay from "../ProgressBarOverlay.vue";
import util from "../../util";
import moment from "moment";
import { initLineListReportGenerator } from "../../services/reportClient";

// Time-period columns the SUMINF summary table can hold. The template declares 14
// fixed-width value columns (13 periods + Total) filling the landscape page, and
// Carbone cannot repeat ODT <table:table-column> declarations, so this is a hard
// ceiling -- cells past it are dropped by LibreOffice, not shrunk to fit. Periods
// beyond the limit are reported in `note` instead. Keep in sync with the template.
const PERIOD_LIMIT = 13;

// Mirrors Surveillance.vue's `lineListReport()`: paginates infection_api/search
// with the page's filters, enriches each case (labs, immunizations, treatments,
// precautions), maps each record into the surveillance report payload, then hands
// the data to the report generator using the summary infection ("SUMINF") template.
// Shared by the Analysis pages (Infection Case Frequency and Infection Rates).
export default {
  name: "surveillance-report",
  props: ["disabled", "filters", "summaryRows", "summaryColumns", "summaryGroupBy"],
  components: {
    ProgressOverlay
  },
  data() {
    return {
      inProgress: false,
      cancelDownload: false,
      progressValue: 0,
      medicationsLookup: {}
    };
  },
  computed: {
    sites() {
      return this.$configStore.data.sites || [];
    },
    precautions() {
      return this.$configStore.data.precautions || [];
    },
    // Flattens the page's Summary table (rows keyed by dynamic time period, each
    // period holding `{ count }`) into the positional arrays the SUMINF template
    // loops over:
    //   header: {d[i=0].summary.groupByLabel}, {d[i=0].summary.columns[i].label}
    //   body:   {d[i=0].summary.rows[i].label}, {d[i=0].summary.rows[i].cells[i].value}, {d[i=0].summary.rows[i].total}
    //   footer: {d[i=0].summary.total.label}, {d[i=0].summary.total.cells[i].value}, {d[i=0].summary.total.total}
    //   note:   {d[i=0].summary.note}
    // The page's "Total" row is split out of `rows` into `total` so the template can
    // shade it as a footer instead of styling it like a group row.
    summary() {
      const pageRows = this.summaryRows || [];
      const allColumns = this.summaryColumns || [];
      const columns = allColumns.slice(0, PERIOD_LIMIT);
      const truncated = allColumns.length - columns.length;

      // The page appends its own summation row last; everything before it is a group.
      const isTotalRow = row => row === pageRows[pageRows.length - 1] && row.label === "Total";
      const toRow = row => ({
        label: row.label,
        cells: columns.map(colKey => ({ value: row[colKey] ? row[colKey].count : 0 })),
        total: row.summationColumn || 0
      });

      return {
        groupByLabel: this.summaryGroupBy || "",
        columns: columns.map(label => ({ label })),
        rows: pageRows.filter(row => !isTotalRow(row)).map(toRow),
        total: pageRows.filter(isTotalRow).map(toRow)[0] || { label: "Total", cells: [], total: 0 },
        note: truncated ? `Showing the first ${columns.length} of ${allColumns.length} time periods; the Total column covers all ${allColumns.length}. Narrow the date range or choose a coarser time scale to see every period.` : ""
      };
    }
  },
  methods: {
    cancel() {
      this.inProgress = false;
      this.cancelDownload = true;
    },
    isStaff(client) {
      return client ? client.clientType === "Staff" : false;
    },
    getFacilityNameForResident(id) {
      const site = this.sites.find(s => s.id === id);
      return site ? site.displayText : "";
    },
    getFacilityNamesForStaff(ids) {
      return this.sites.filter(s => (ids || []).includes(s.id)).map(s => s.displayText);
    },
    precautionById(id) {
      const precaution = this.precautions.find(p => p.id === id);
      return precaution ? precaution.displayText : "";
    },
    evaluateCriteria(row) {
      return row.hasRulesets && row.lastRulesRunDate ? !!row.ruleIds.length : null;
    },
    labResult(caseData) {
      let result = "";
      (caseData.labRecords || []).forEach(l => {
        if (l.organism === caseData.primaryOrganism) {
          result = l.result || "";
        }
      });
      return result;
    },
    async enrichPage(page) {
      const response = await this.$http
        .post(`${window.CONFIG.infection_api}/search`, {
          ...this.filters,
          orderBy: "onsetDate",
          page,
          size: 30
        })
        .then(resp => resp.json());
      const elements = response.content;
      for (let i = 0; i < elements.length; i++) {
        const labRecordsResponse = await this.$http.get(`${window.CONFIG.legacy_facade_api}/symptomDetail/${elements[i].id}/labs`);
        this.bumpProgress(response.totalElements);

        const immunizationDetailsResponse = await this.$http.get(`${window.CONFIG.legacy_facade_api}/clients/${elements[i].client.id}/immunizations`);
        this.bumpProgress(response.totalElements);

        const treatmentDetailsResponse = await this.$http
          .get(`${window.CONFIG.treatement_api}/prescriptions`, {
            params: {
              clientId: elements[i].client.id,
              symptomDetailId: elements[i].id
            }
          })
          .then(resp => resp.json());

        const precautionDetailsResponse = await this.$http
          .get(window.CONFIG.precaution_api, {
            params: {
              clientId: elements[i].client.id,
              symptomDetailId: elements[i].id
            }
          })
          .then(resp => resp.json());
        this.bumpProgress(response.totalElements);

        elements[i].labRecords = labRecordsResponse.body || [];
        elements[i].immunizationDetails = immunizationDetailsResponse.body || [];
        elements[i].medicineDetails = treatmentDetailsResponse.content || [];
        elements[i].client.precautionDetails = precautionDetailsResponse.content || [];
      }
      return response;
    },
    bumpProgress(totalElements) {
      this.processedElements++;
      const denominator = (totalElements || 0) * 3;
      this.progressValue = denominator ? Math.round(100 * (this.processedElements / denominator)) : 0;
    },
    async lineListReport() {
      this.inProgress = true;
      this.progressValue = 0;
      this.cancelDownload = false;
      this.processedElements = 0;
      const meds = this.medicationsLookup;
      // Snapshot once and share the reference across every row, the same way the
      // report-level filter dates are repeated on each record.
      const summary = this.summary;

      const formatDate = timestamp => (timestamp ? moment(timestamp).format(this.$configStore.dateFormat()) : "");
      const formatFac = _ => (this.isStaff(_.client) ? this.getFacilityNamesForStaff(_.client.facilities).join(", ") : this.getFacilityNameForResident(_.client.facId));
      const getPrimary = _ => (_.labRecords || []).filter(r => r.organism === _.primaryOrganism)[0];
      const getCultureDate = _ => {
        const p = getPrimary(_);
        return p ? formatDate(p.cultureDate) : "";
      };
      const renderMedications = _ => {
        return (_.medicineDetails || [])
          .map(t => {
            const { startDate, endDate, frequency, dosage, medicationId } = t;
            const medication = meds[medicationId]?.displayText;
            return `${medication || ""} ${dosage || ""} ${frequency || ""}\n${formatDate(startDate)} - ${formatDate(endDate)}`;
          })
          .join("\n");
      };

      const convertDataToColumns = _ => {
        const { client, staffIsolation, isolationStart, isolationEnd, location, labRecords, onsetDate, outcome, resolutionDate, infectionSite, infectionType, status, immunizationDetails, symptoms, primaryOrganism, notes, origin, notifications } = _;
        const { firstName, middleName, lastName, gender, dateOfBirth, staffId, precautionDetails } = client;

        const patientIdLookupKey = this.$configStore.patientIdType() || "patientId";
        const { createDateFrom, updateDateFrom, onsetDateFrom, createDateTo, updateDateTo, onsetDateTo } = this.filters || {};

        return {
          filterDateStart: moment(createDateFrom || updateDateFrom || onsetDateFrom).format("YYYY-MM-DD"),
          filterDateEnd: moment(createDateTo || updateDateTo || onsetDateTo).format("YYYY-MM-DD"),
          reportDate: formatDate(Date.now()),
          summary,
          facility: formatFac(_),
          name: `${firstName} ${lastName}`,
          firstName,
          lastName,
          middleName,
          firstInitial: (firstName ?? "")[0],
          middleInitial: (middleName ?? "")[0],
          lastInitial: (lastName ?? "")[0],
          id: this.isStaff(client) ? staffId : client[patientIdLookupKey],
          gender: gender,
          dob: dateOfBirth ? formatDate(dateOfBirth) : "",
          location: location ? location : "",
          onsetDate: onsetDate ? formatDate(onsetDate) : "",
          outcome: outcome,
          resolutionDate: resolutionDate ? formatDate(resolutionDate) : "",
          infectionSite,
          infectionType,
          status: status ? "confirmed" : "suspected",
          immunizationDetails: (immunizationDetails || []).map(d => `${d.description}${d.vaccineType ? ` (${d.vaccineType}) ` : " "}${formatDate(d.date)}`).join("\n"),
          precautions: (precautionDetails || []).map(p => this.precautionById(p.precautionId)).join("\n"),
          precautionStartDates: (precautionDetails || []).map(({ startDate }) => formatDate(startDate)).join("\n"),
          precautionEndDates: (precautionDetails || []).map(({ endDate }) => formatDate(endDate)).join("\n"),
          symptoms: (symptoms || []).map(s => s.displayText).join("\n"),
          cultureDate: getCultureDate(_),
          result: primaryOrganism ? this.labResult(_) : "",
          organism: primaryOrganism,
          treatments: renderMedications(_),
          notes: (notes || []).map(n => n.content).join(", "),
          origin: origin === "Not Selected" ? "" : origin,
          criteria: this.evaluateCriteria(_) === true ? "Criteria Met" : this.evaluateCriteria(_) === false ? "Criteria Not Met" : "",
          notifications: (notifications || []).filter(n => n.notified).map(n => (n.notifiedDate ? `${n.displayText} (${formatDate(n.notifiedDate)})` : n.displayText)).join("\n"),
          labs: (labRecords || []).map(l => {
            return {
              ...l,
              updated: formatDate(l.updated),
              cultureDate: formatDate(l.cultureDate),
              collectionDate: formatDate(l.collectionDate)
            };
          }),
          staffIsolation: this.isStaff(client) ? staffIsolation : "",
          staffIsolationStart: this.isStaff(client) ? formatDate(isolationStart) : "",
          staffIsolationEnd: this.isStaff(client) ? formatDate(isolationEnd) : ""
        };
      };

      let data = [];
      let currentPage = 1;
      let hasMore = true;
      while (hasMore) {
        if (this.cancelDownload) {
          break;
        }
        const json = await this.enrichPage(currentPage);
        data = [...data, ...json.content.map(convertDataToColumns)];
        if (json.totalPages === 0) {
          this.cancelDownload = true;
          break;
        }
        hasMore = currentPage < json.totalPages;
        currentPage++;
      }
      this.progressValue = 100;
      this.inProgress = false;
      data = data.sort((a, b) => b.onsetDate - a.onsetDate);
      if (!this.cancelDownload) {
        const { summaryInfection } = this.$configStore.getLineListReports();
        initLineListReportGenerator(summaryInfection, data);
      }
    }
  },
  async created() {
    this.processedElements = 0;
    this.medicationsLookup = await this.$http
      .get(`${window.CONFIG.treatement_api}/config`)
      .then(resp => resp.json())
      .then(json => [...json.medications, ...json.archivedMedications])
      .then(meds => util.arrayToObj(meds, "id"))
      .catch(() => ({}));
  }
};
</script>

<style scoped></style>
